import React, { useEffect, useMemo, useRef, useState } from "react";
import "card-pattern/dist/main.css";
import {
  LandingCard,
  cardLinkList,
  getFilteredLandingCards,
  getResourcesLandingCards,
} from "../../../config/landingCards";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Notifications from "../../../components/common/Notifications";
import { useAppSelector } from "../../../store/hooks";
import Loader from "../../../components/common/Loader";
import { useEmployee } from "../../../contexts/EmployeeContext";
import { useMonthlyReportLink } from "../../../hooks/useMonthlyReportLink";


const UBTLandingPage: React.FC = () => {
  const primaryRole = useAppSelector(
    (state) => state.profile.profile?.primaryRole ?? ""
  );
  const profileLoading = useAppSelector((state) => state.profile.loading);
  const isEmployeeNotFound = useAppSelector(
    (state) => state.employee.isEmployeeNotFound
  );
  const { loadingEmployee } = useEmployee();
  const { link: monthlyReportLink } = useMonthlyReportLink();
  const visibleCards = useMemo(
    () => getFilteredLandingCards(primaryRole),
    [primaryRole]
  );
  const resourcesLandingCards = useMemo(
    () => getResourcesLandingCards(monthlyReportLink),
    [monthlyReportLink]
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [showDeleteSuccessBanner, setShowDeleteSuccessBanner] = useState(false);
  // Capture the flag once at component initialisation — before any effects run.
  // Using a ref prevents the effect from re-firing when navigate() clears location.state,
  // which would cancel the 5-second timer and keep the banner visible indefinitely.
  const hasPendingDeleteSuccess = useRef(
    location.state?.teamDeleteSuccess === true
  );

  useEffect(() => {
    if (!hasPendingDeleteSuccess.current) return;
    setShowDeleteSuccessBanner(true);
    // Clear navigation state so the banner doesn't reappear on refresh
    navigate(location.pathname, { replace: true, state: {} });
    const timer = setTimeout(() => setShowDeleteSuccessBanner(false), 5000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount to process navigation state


  if (profileLoading || loadingEmployee) {
    return <Loader />;
  }

  return (
    <div className="min-h-[70vh] lg:mx-40! mx-5!">
      <div className="padding-bottom-2u margin-top-4u desktop:mt-18! tablet:mt-12.5! mobile:mt-8!">
        <h1 className="greeting mb-19!">Unit Based Teams</h1>
        {/* Show only error notification if employee not found, else normal banners and content */}
        {isEmployeeNotFound ? (
          <div className="w-[80%] mb-4!">
            <Notifications
              variant="Error"
              heading="Employee not found"
            />
          </div>
        ) : (
          <>
            {showDeleteSuccessBanner && (
              <div className="w-[80%] mb-4!">
                <Notifications
                  variant="Success"
                  heading="Team Deleted Successfully"
                />
              </div>
            )}
            <div className="tablet:-mt-3.5! mobile:mt-0!">
              {visibleCards.map((card: LandingCard, idx: number) => (
                <div className="my-15!" key={card.title}>
                  <h2 className="mb-6! mt-0!">{card.title}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3! gap-x-4 gap-y-6 w-full mx-auto">
                    {card.linkList.map((cardLink: cardLinkList) => (
                      <fieldset
                        className={`ds-card w-full m-0! p-0! shadow-card`}
                        key={cardLink.link}
                        data-ds-theme="vessel"
                        data-ds-variant="thumbnail"
                        data-ds-version="2"
                        aria-labelledby={`card-${idx}-title`}
                      >
                        <div className="flex w-full flex-col m-4!">
                          <div>
                            <div className="flex">
                              <div className="ds-card__image">
                                <img
                                  src={cardLink.icon}
                                  alt="card icons"
                                  className="h-11! w-11!"
                                />
                              </div>
                              <div className="ds-card__content">
                                <div
                                  className="p3 w-full -medium text-[16px]!"
                                  id={`card-${idx}-title`}
                                >
                                  {cardLink.linkTitle}
                                </div>
                                <div className="ds-card__link-container mt-0! pt-0!">
                                  <div className="text-[14px] py-2!">
                                    {cardLink.description}
                                  </div>
                                  <div className="ds-card__text-link">
                                    {cardLink.link.startsWith("http") ? (
                                      <a
                                        href={cardLink.link}
                                        target={cardLink.target}
                                        rel="noopener noreferrer"
                                        className="inline-link"
                                      >
                                        {cardLink.linkText ?? "Open"}
                                      </a>
                                    ) : (
                                      <Link
                                        to={cardLink.link}
                                        className="inline-link no-underline!"
                                      >
                                        {cardLink.linkText ?? "Open"}
                                      </Link>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </fieldset>
                    ))}
                  </div>
                </div>
              ))}
              <div className="my-15!">
                <h2 className="mb-4! mt-0!">Resources</h2>
                <div className="grid grid-cols-1 md:grid-cols-2! gap-x-4 gap-y-6 w-full mx-auto">
                  <fieldset
                    className="ds-card w-full m-0! p-0! shadow-card"
                    data-ds-theme="vessel"
                    data-ds-variant="thumbnail"
                    data-ds-version="2"
                    aria-labelledby="card-2-title"
                  >
                    <div className="flex w-full flex-col p-4! m-6!">
                      <div>
                        <div className="flex">
                          <div className="ds-card__content pl-0!">
                            <h3 className="mb-0!"> Useful Links</h3>
                            {resourcesLandingCards.map(
                              (linkItem: cardLinkList, index: number) => (
                                <div
                                  className="ds-card__link-container mt-6! pt-0!"
                                  key={`resource-${index}-${linkItem.linkText}`}
                                >
                                  {linkItem.link && (
                                    <div className="ds-card__text-link pb-0!">
                                      <a
                                        className="inline-link no-underline! hover:underline!"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={linkItem.link}
                                      >
                                        {linkItem.linkText}
                                      </a>
                                    </div>
                                  )}
                                  {linkItem.description && (
                                    <div className="text-[14px]">
                                      {linkItem.description}
                                    </div>
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </fieldset>
                  <fieldset
                    className="ds-card w-full m-0! self-start p-0! shadow-card"
                    data-ds-theme="vessel"
                    data-ds-variant="thumbnail"
                    data-ds-version="2"
                    aria-labelledby="card-2-title"
                  >
                    <div className="flex w-full flex-col! p-4!  m-6!">
                      <div>
                        <div className="flex">
                          <div className="ds-card__content pl-0!">
                            <div className="p3 w-full -medium text-[16px]!">
                              Items not showing?
                            </div>
                            <div className="ds-card__link-container mt-0! pt-0!">
                              <div className="text-[14px] py-2! leading-5!">
                                If your teams aren't being displayed under My Teams
                                page or some of your UBT Tracker menu items are
                                missing.
                              </div>
                              <div className="ds-card__text-link flex align-middle">
                                <a
                                  className="inline-link no-underline! hover:underline!"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href="/assets/landingPagePDF/Missing_Info_UBT_Tracker.docx"
                                >
                                  <img
                                    src="/assets/images/trouble-shoot.svg"
                                    alt="card icons"
                                    height={24}
                                    width={216}
                                  />{" "}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex pt-5.5!">
                          <div className="ds-card__content pl-0!">
                            <div className="p3 w-full -medium text-[16px]!">
                              What is UBT Tracker?
                            </div>
                            <div className="ds-card__link-container mt-0! pt-0! leading-5!">
                              <p className="text-[14px] mt-2!">
                                The UBT Tracker provides you with information on
                                what unit-based teams are doing at your facility and
                                throughout your region.
                              </p>
                              <div className="text-[14px] mb-0!">
                                It is a convenient way to keep a team's profile
                                current and create a snapshot of accomplishments.
                                This simple-to-use, efficient tool:
                                <ul className="list-disc list-inside ml-2! mt-0! leading-5!">
                                  <li className="b">
                                    Creates a real-time database of UBT information
                                  </li>
                                  <li>Makes updating information simple</li>
                                  <li>Streamlines reporting</li>
                                  <li>
                                    Eliminates last minute pressure to gather
                                    information for reports
                                  </li>
                                  <li>
                                    Simplifies sharing information about your team
                                    and learning from other teams
                                  </li>
                                  <li>
                                    Highlights accomplishments for local, regional
                                    and national leaders
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="mb-10!">
        <div className="-mb-27! tablet:hidden">
          <ScrollToTopBtn />
        </div>
      </div>
    </div>
  );
};

export default UBTLandingPage;
